import { useState } from "react";
import { useLocation } from "wouter";
import { ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export interface HeaderSplitNavEntry {
  label: string;
  href?: string;
  icon: React.ComponentType<{ className?: string }>;
  roles?: string[];
  badge?: number;
  onClick?: () => void;
}

interface HeaderSplitNavProps {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  entries: HeaderSplitNavEntry[];
  badge?: number;
  activeRoutes: string[];
  testId: string;
  /** Extra items rendered after a separator at the bottom of the menu (e.g. a pop-out action). */
  extraItems?: React.ReactNode;
}

export function HeaderSplitNav({
  label,
  icon: Icon,
  href,
  entries,
  badge,
  activeRoutes,
  testId,
  extraItems,
}: HeaderSplitNavProps) {
  const [location, navigate] = useLocation();
  const [open, setOpen] = useState(false);

  const isActive = activeRoutes.some((r) => location === r || location.startsWith(r + "/"));

  return (
    <div className="flex items-center">
      <button
        onClick={() => navigate(href)}
        className={`relative flex items-center gap-1.5 h-9 pl-3 pr-2 rounded-l-full text-sm font-medium transition-colors ${
          isActive
            ? "bg-primary text-primary-foreground ring-2 ring-primary/35"
            : "bg-primary hover:bg-primary/90 text-primary-foreground"
        }`}
        title={label}
        data-testid={`button-${testId}-nav`}
      >
        <Icon className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">{label}</span>
        {badge !== undefined && badge > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 bg-destructive text-destructive-foreground rounded-full text-xs font-bold flex items-center justify-center border-2 border-card">
            {badge > 9 ? "9+" : badge}
          </span>
        )}
      </button>

      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <button
            className={`flex items-center justify-center h-9 w-6 rounded-r-full border-l border-primary-foreground/25 text-primary-foreground transition-colors ${
              isActive ? "bg-primary ring-2 ring-primary/35" : "bg-primary hover:bg-primary/90"
            }`}
            title={`${label} menu`}
            data-testid={`button-${testId}-nav-menu`}
          >
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          {entries.map((e) => (
            <DropdownMenuItem
              key={e.label}
              onClick={() => (e.onClick ? e.onClick() : e.href && navigate(e.href))}
            >
              <e.icon className="w-4 h-4 mr-2" />
              <span className="flex-1">{e.label}</span>
              {e.badge !== undefined && e.badge > 0 && (
                <span className="ml-2 bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                  {e.badge > 9 ? "9+" : e.badge}
                </span>
              )}
            </DropdownMenuItem>
          ))}
          {extraItems && (
            <>
              <DropdownMenuSeparator />
              {extraItems}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
